import { useState, useEffect } from 'react';
import { Flex, Form, Upload, message } from 'antd';
import TextField from '../form/text-field/text-field';
import { TextArea } from '../form/textarea/textarea';
import Dropdown from '../form/dropdown/dropdown';
import { Button } from '../button/button';
import { usePublicSupport } from '@/hooks/use-support/use-support';
import styles from './public-support.module.scss';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export type PublicSupportFormFieldType = {
  requester_name: string;
  requester_email: string;
  subject: string;
  message: string;
  category?: string;
};

export function PublicSupport() {
  const { t } = useTranslation(['support', 'auth', 'common']);
  const [form] = Form.useForm();
  const { submitPublicTicket, isSubmittingTicket, isTicketSubmitted, isTicketError, ticketError, ticketData, resetTicket } =
    usePublicSupport();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (isTicketSubmitted && ticketData) {
      messageApi.open({
        type: 'success',
        content: t('support:form.success', { ticketNumber: ticketData.ticket_number }),
      });
      form.resetFields();
      setFileList([]);
      resetTicket();
    }
  }, [isTicketSubmitted, ticketData, form, resetTicket, messageApi, t]);

  const categoryOptions = [
    { label: t('support:form.categories.common'), value: 'common' },
    { label: t('support:form.categories.removeAccount'), value: 'remove_account' },
    { label: t('support:form.categories.removeData'), value: 'remove_data' },
  ];

  const handleSubmit = (values: PublicSupportFormFieldType) => {
    const files = fileList.map((file) => file.originFileObj as File).filter(Boolean);
    const tags = values.category ? [values.category] : undefined;

    submitPublicTicket({
      support_ticket: {
        subject: values.subject,
        message: values.message,
        requester_name: values.requester_name,
        requester_email: values.requester_email,
        tags,
      },
      attachments: files.length > 0 ? files : undefined,
    });
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1);
    setFileList(newFileList);
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
    resetTicket();
  };

  const getErrorMessage = () => {
    if (ticketError && ticketError instanceof AxiosError) {
      const errorData = ticketError.response?.data;
      if (errorData && typeof errorData === 'object' && 'error' in errorData) {
        const err = errorData.error;
        if (typeof err === 'object' && err !== null && 'message' in err) {
          return String((err as any).message);
        }
        return String(err);
      }
      return ticketError.message || t('support:form.submitFailed');
    }
    return t('support:form.submitFailed');
  };

  return (
    <Form form={form} layout="vertical" className={styles.formContainer} onFinish={handleSubmit}>
      {contextHolder}
      <Flex vertical gap={16}>
        <Form.Item<PublicSupportFormFieldType>
          label={t('support:form.name')}
          name="requester_name"
          rules={[{ required: true, message: t('support:form.nameRequired') }]}
        >
          <TextField placeholder={t('support:form.namePlaceholder')} />
        </Form.Item>

        <Form.Item<PublicSupportFormFieldType>
          label={t('auth:fields.email')}
          name="requester_email"
          rules={[
            { required: true, message: t('common:validation.emailRequired') },
            { type: 'email', message: t('common:validation.emailInvalid') },
          ]}
        >
          <TextField placeholder={t('support:form.emailPlaceholder')} type="email" />
        </Form.Item>

        <Form.Item<PublicSupportFormFieldType>
          label={t('support:form.subject')}
          name="subject"
          rules={[{ required: true, message: t('support:form.subjectRequired') }]}
        >
          <TextField placeholder={t('support:form.subjectPlaceholder')} />
        </Form.Item>

        <Form.Item<PublicSupportFormFieldType>
          label={t('support:form.message')}
          name="message"
          rules={[{ required: true, message: t('support:form.messageRequired') }]}
        >
          <TextArea placeholder={t('support:form.messagePlaceholder')} rows={6} />
        </Form.Item>

        <Form.Item<PublicSupportFormFieldType> label={t('support:form.category')} name="category">
          <Dropdown placeholder={t('support:form.categoryPlaceholder')} options={categoryOptions} />
        </Form.Item>

        <Form.Item label={t('support:form.attachment')}>
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept="image/*"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} buttonVariant="secondary">
              {t('support:form.uploadImage')}
            </Button>
          </Upload>
        </Form.Item>

        {isTicketError && <div className={styles.errorMessage}>{getErrorMessage()}</div>}

        <Form.Item>
          <Flex gap={10}>
            <Button type="primary" htmlType="submit" loading={isSubmittingTicket}>
              {t('support:form.submit')}
            </Button>
            <Button buttonVariant="secondary" onClick={handleReset} disabled={isSubmittingTicket}>
              {t('common:actions.clear')}
            </Button>
          </Flex>
        </Form.Item>
      </Flex>
    </Form>
  );
}
